/**
 * Non-namespaced storage for an affiliator referral code captured from a `?ref=CODE`
 * link on a public landing page — BEFORE the visitor has a userId. The app's other
 * storage helpers key on the logged-in userId, so they can't be used pre-login.
 *
 * The code survives navigation via localStorage and the Auth0 login round-trip via
 * `appState.returnTo` (which preserves the query string). It is read once after login
 * to claim the lead, then cleared.
 */
const REF_STORAGE_KEY = 'imprv.affiliate.ref';

const readReferralFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  const code = new URLSearchParams(window.location.search).get('ref');
  const trimmed = code?.trim();
  return trimmed ? trimmed : null;
};

const captureReferralCode = (code: string | null | undefined): void => {
  if (typeof window === 'undefined' || !code) return;
  try {
    window.localStorage.setItem(REF_STORAGE_KEY, code);
  } catch {
    // storage may be unavailable (private mode / disabled) — attribution is best-effort
  }
};

const getStoredReferralCode = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(REF_STORAGE_KEY);
  } catch {
    return null;
  }
};

const clearReferralCode = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(REF_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Capture the `?ref=` param from the current URL into storage (if present). */
const captureReferralFromUrl = (): void => {
  captureReferralCode(readReferralFromUrl());
};

export {
  captureReferralCode,
  captureReferralFromUrl,
  clearReferralCode,
  getStoredReferralCode,
  readReferralFromUrl
};
